import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FileText, Upload, Download, Trash2, Search, Filter,
  File, Image, Video, Music, Table, Presentation, X,
  CheckCircle, Lock, Users, Eye, AlertCircle
} from 'lucide-react';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MATERIAL_TYPE_ICONS = {
  pdf: FileText,
  document: File,
  presentation: Presentation,
  spreadsheet: Table,
  image: Image,
  video: Video,
  audio: Music,
  other: File
};

const MATERIAL_TYPE_LABELS = {
  pdf: 'PDF',
  document: 'Documento',
  presentation: 'Presentación',
  spreadsheet: 'Hoja de Cálculo',
  image: 'Imagen',
  video: 'Video',
  audio: 'Audio',
  other: 'Otro'
};

const ACCESS_TIER_LABELS = {
  public: 'Público',
  free_user: 'Usuario Gratuito',
  paying: 'Miembros de Pago',
  vip: 'Solo VIP',
  ponente: 'Solo Ponentes',
  admin: 'Solo Admin'
};

const ACCESS_TIER_COLORS = {
  public: 'bg-gray-100 text-gray-700',
  free_user: 'bg-blue-100 text-blue-700',
  paying: 'bg-green-100 text-green-700',
  vip: 'bg-purple-100 text-purple-700',
  ponente: 'bg-yellow-100 text-yellow-700',
  admin: 'bg-red-100 text-red-700'
};

export default function AdminMaterials() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [materials, setMaterials] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    access_tier: 'paying',
    webinar_id: '',
    video_id: ''
  });
  const [error, setError] = useState('');

  const token = localStorage.getItem('upim_token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadMaterials();
  }, [navigate, token, filterType]);

  const loadMaterials = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterType) params.append('material_type', filterType);
      params.append('limit', '50');

      const response = await axios.get(`${API_URL}/api/content/materials?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMaterials(response.data.materials || []);
    } catch (error) {
      console.error('Error loading materials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (100MB max)
      if (file.size > 100 * 1024 * 1024) {
        setError('El archivo excede el límite de 100MB');
        return;
      }
      setSelectedFile(file);
      if (!uploadData.title) {
        setUploadData(prev => ({ ...prev, title: file.name.replace(/\.[^/.]+$/, '') }));
      }
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      setError('Selecciona un archivo');
      return;
    }

    try {
      setUploading(true);
      setError('');

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', uploadData.title);
      formData.append('description', uploadData.description || '');
      formData.append('access_tier', uploadData.access_tier);
      if (uploadData.webinar_id) formData.append('webinar_id', uploadData.webinar_id);
      if (uploadData.video_id) formData.append('video_id', uploadData.video_id);

      const response = await axios.post(`${API_URL}/api/content/materials/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      if (response.data.warning) {
        alert(response.data.warning);
      }

      setShowUploadModal(false);
      setSelectedFile(null);
      setUploadData({ title: '', description: '', access_tier: 'paying', webinar_id: '', video_id: '' });
      setUploadProgress(0);
      loadMaterials();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al subir archivo');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (materialId, fileName) => {
    try {
      const response = await axios.get(`${API_URL}/api/content/materials/${materialId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Open download URL in new tab
      window.open(`${API_URL}${response.data.download_url}`, '_blank');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al descargar');
    }
  };

  const handleDelete = async (materialId) => {
    if (!window.confirm('¿Eliminar este material?')) return;

    try {
      await axios.delete(`${API_URL}/api/content/materials/${materialId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadMaterials();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const filteredMaterials = materials.filter(m =>
    m.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.file_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="p-6" data-testid="admin-materials-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-7 h-7 text-blue-500" />
              Materiales de Contenido
            </h1>
            <p className="text-gray-500 text-sm">PDFs, presentaciones y documentos para webinars</p>
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
            data-testid="upload-material-btn"
          >
            <Upload className="w-4 h-4" />
            Subir Material
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar materiales..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="search-materials-input"
              />
            </div>
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="filter-type-select"
          >
            <option value="">Todos los tipos</option>
            {Object.entries(MATERIAL_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-xl border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{materials.length}</p>
                <p className="text-sm text-gray-500">Total Materiales</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <File className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {materials.filter(m => m.material_type === 'pdf').length}
                </p>
                <p className="text-sm text-gray-500">PDFs</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Presentation className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {materials.filter(m => m.material_type === 'presentation').length}
                </p>
                <p className="text-sm text-gray-500">Presentaciones</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Download className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {materials.reduce((acc, m) => acc + (m.download_count || 0), 0)}
                </p>
                <p className="text-sm text-gray-500">Descargas Totales</p>
              </div>
            </div>
          </div>
        </div>

        {/* Materials Grid */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Cargando materiales...</div>
        ) : filteredMaterials.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
            <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No hay materiales subidos</p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="mt-4 text-blue-500 hover:text-blue-600 font-medium"
            >
              Subir el primer material
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMaterials.map(material => {
              const IconComponent = MATERIAL_TYPE_ICONS[material.material_type] || File;
              return (
                <div key={material.id} className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-3">
                    <div className="p-3 bg-gray-100 rounded-lg shrink-0">
                      <IconComponent className="w-6 h-6 text-gray-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate" title={material.title}>
                        {material.title}
                      </h3>
                      <p className="text-sm text-gray-500 truncate">{material.file_name}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${ACCESS_TIER_COLORS[material.access_tier] || 'bg-gray-100 text-gray-600'}`}>
                          {ACCESS_TIER_LABELS[material.access_tier] || material.access_tier}
                        </span>
                        <span className="text-xs text-gray-400">
                          {material.file_size_mb} MB
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {material.description && (
                    <p className="text-sm text-gray-500 mt-3 line-clamp-2">{material.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                    <div className="text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Download className="w-3 h-3" />
                        {material.download_count || 0} descargas
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDownload(material.id, material.file_name)}
                        className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Descargar"
                        data-testid={`download-${material.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(material.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar"
                        data-testid={`delete-${material.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Upload className="w-5 h-5 text-blue-500" />
                    Subir Material
                  </h3>
                  <button 
                    onClick={() => setShowUploadModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleUpload} className="p-6 space-y-4">
                {error && (
                  <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}

                {/* File Drop Zone */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                    selectedFile ? 'border-green-300 bg-green-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.mp4,.mp3"
                    data-testid="file-input"
                  />
                  {selectedFile ? (
                    <div>
                      <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-2" />
                      <p className="font-medium text-gray-900">{selectedFile.name}</p>
                      <p className="text-sm text-gray-500">
                        {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                      <p className="font-medium text-gray-700">Haz clic para seleccionar un archivo</p>
                      <p className="text-sm text-gray-500 mt-1">
                        PDF, Word, PowerPoint, Excel, imágenes, videos (máx. 100MB)
                      </p>
                      <p className="text-xs text-gray-400 mt-2">
                        Recomendado: menos de 15MB para mejor rendimiento
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Título *
                  </label>
                  <input
                    type="text"
                    value={uploadData.title}
                    onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Título del material"
                    required
                    data-testid="material-title-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción
                  </label>
                  <textarea
                    value={uploadData.description}
                    onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Breve descripción del contenido"
                    data-testid="material-description-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nivel de Acceso
                  </label>
                  <select
                    value={uploadData.access_tier}
                    onChange={(e) => setUploadData({ ...uploadData, access_tier: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="access-tier-select"
                  >
                    {Object.entries(ACCESS_TIER_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Define quién puede descargar este material
                  </p>
                </div>

                {/* Upload progress */}
                {uploading && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subiendo...</span>
                      <span className="font-medium">{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowUploadModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
                    disabled={uploading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={!selectedFile || uploading}
                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    data-testid="submit-upload-btn"
                  >
                    {uploading ? 'Subiendo...' : 'Subir Material'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
